/* eslint-disable jsx-a11y/anchor-is-valid */
/**
@format
@flow
*/
import React from 'react';
import styled from 'styled-components';
import { Heading } from '@ziynx/components/typography';
import { ButtonGroup } from '@ziynx/components/buttons';
import Colors from '@ziynx/theme/colors';
import ListItem from '@ziynx/components/listItems';
import Link from '@ziynx/components/links';
import Icon from '@ziynx/components/icons';
import Tag from '@ziynx/components/indicators';
import { withAppManager, getDimensions } from '@ziynx/components/utilities';
import type { ManagedProps } from '@ziynx/flow-types/appManager';
import type { ListItem as ListItemType } from '@ziynx/flow-types/listItems';
import type { ButtonGroup as ButtonGroupType } from '@ziynx/flow-types/buttons';
import type { Viewports } from '@ziynx/flow-types/globals';
import * as KrispyTokens from '@krispy-tokens/blue';
import CardBase from './CardBase';
import CardVideo from './CardVideo';

type TagType = {
  label: string,
  variant: string,
}

export type Props = $ReadOnly<{|
  /**
   * When scrolled, card transitions into a different layout.
   */
  isScrolled?: boolean,
  /**
   * Boolean to allow for overriding the overlay
   */
  hideOverlay?: boolean,
  /**
   * videoSource for cardVideo
   */
  src?: string,
  /**
   * ImageOverlay source for cardVideo
   */
  imageSource?: string,
  /**
   * Eyebrow text - graduation month
   */
  eyebrow?: string,
  /**
   * Title
   */
  title?: string,
  /**
   * Object containing Link props.
   */
  link?: Link,
  /**
   * Subtitle
   */
  subtitle?: string,
  /**
   * Tag
   */
  tag?: TagType,
  /**
   * Override the width of the card
   */
  cardWidth?: string | number,
  /**
   * List items
   */
  listItems?: Array<ListItemType>,
  /**
   * Data array for buttonGroup
   */
  buttonGroup?: ButtonGroupType,
   /**
   * Allow to pass in ref. Optional
   */
  getRef?: any,
  /**
   * Custom ononTimeUpdate function to grab
   * video's current playback time
   */
  onTimeUpdate?: () => void,
   /**
   * Custom function fired when video is played
   */
  onPlay?: () => void,
  /**
   * Custom function fired when user pauses video
   */
  onPause?: () => void,
  /**
   * Custom onClick function fired when user taps
   * on the imageOverlay
   */
  onClick?: () => void,
  /**
   * Custom function fired when video ends
   */
  onEnd?: () => void,
  /**
  * Fires on seek end
  */
  onSeekEnd?: () => void,
  /**
  * Fires on seek
  */
  onSeek?: () => void,
  /**
   * If set to true, card width will not respond to breakpoint and remain fixed.
   */
  fixedWidth?: boolean,
  /**
   * Size Prop
   */
  size?: 'extraSmall' | 'small' | 'medium' | 'large',
  /**
   * Viewports
   */
  viewport?: Viewports,
|}>;

const findWidth = (size, fixedWidth, isScrolled) => {
  if (!fixedWidth && !isScrolled) return '100%';
  if (isScrolled) return '288px';
  switch (size) {
  case 'extraSmall':
    return '288px';
  case 'small':
    return '382px';
  case 'medium':
    return '520px';
  case 'large':
  default:
    return '688px';
  }
};

const StyledCard = styled(CardBase)`
  box-sizing: border-box;
  min-width: ${({ isMobile }) => (isMobile ? '288px' : '688px')};
  max-width: ${({ isMobile }) => (isMobile ? '687px' : '688px')};
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
`;

const VideoSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const CardVideoWrapper = styled.div`
  ${({ isMobile }) => isMobile && 'padding-bottom: 32px;'}
  width: 100%;
`;

const TextSection = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  ${({ isMobile }) => !isMobile && 'padding-left: 32px;'};
  ${({ fixedWidth, isMobile }) => !fixedWidth && !isMobile && 'max-width: 260px;'};
`;

const StyledEyebrow = styled(Heading)`
  padding-bottom: 4px;
`;

const StyledTitle = styled(Heading)`
  padding-bottom: 4px;
`;

const StyledLink = styled.div`;
  display: flex;
  align-items: center;
  gap: 4px;
  padding-bottom: 16px;
`;

const StyledSubtitle = styled(Heading)`
  padding-bottom: ${KrispyTokens.KrispySpacingElementsHdXlarge}px;
`;

const StyledTag = styled.div`
  padding-bottom: ${KrispyTokens.KrispySpacingElementsHdXlarge}px;
`;

const StyledListItem = styled(ListItem)`
  ${({ lastChild }) => !lastChild && 'padding-bottom: 24px;'};
`;

const StyledButtonGroupTop = styled(ButtonGroup)`
  padding-top: 24px;
`;

const StyledButtonGroupBottom = styled(ButtonGroup)`
  padding-bottom: 32px;
`;

/**
 * Renders the list item squircle sections
 */
// eslint-disable-next-line max-len
const renderListItems = (items: Array<ListItemType>): React$Node => items.map((item: ListItemType, index: number) => (
  <StyledListItem
    variant="squircle"
    key={item?._id || index}
    lastChild={index === items.length - 1}
    size="medium"
    squircleText={item?.squircleText}
    label={item?.label}
    text={item?.text}
    title={item?.text}
  />
));

/**
 * CardProfile component
 */
const CardProfile = (props: Props): React$Node => {
  const {
    isScrolled,
    eyebrow,
    title,
    link,
    subtitle,
    tag,
    listItems,
    buttonGroup,
    imageSource,
    src,
    onPlay,
    onSeek,
    onSeekEnd,
    onTimeUpdate,
    fixedWidth,
    size,
    cardWidth: cardWidthProp,
    viewport,
    ...rest
  } = props;

  const {
    children, onClick,
  } = link || {};

  let cardWidth = findWidth(size, fixedWidth, isScrolled);
  if (cardWidthProp) cardWidth = getDimensions(cardWidthProp);

  let isMobile = false;
  if (viewport === 'mobileSmall' || viewport === 'mobile') isMobile = true;
  if (isScrolled) isMobile = true;

  if (!src || !imageSource) return null;
  return (
    <StyledCard width={cardWidth} isMobile={isMobile}>
      <ContentWrapper isMobile={isMobile}>
        <VideoSection>
          <CardVideoWrapper isMobile={isMobile}>
            <CardVideo
              src={src}
              imageSource={imageSource}
              width={isMobile ? '100%' : '348px'}
              onSeek={onSeek}
              onPlay={onPlay}
              onTimeUpdate={onTimeUpdate}
              onSeekEnd={onSeekEnd}
              aspectRatio="1 / 1"
              {...rest}
            />
          </CardVideoWrapper>
          {/** WHEN IN STACKED VIEW */}
          {!!buttonGroup && !isMobile && <StyledButtonGroupTop {...buttonGroup} /> }

        </VideoSection>
        <TextSection isMobile={isMobile} fixedWidth={fixedWidth}>
          <StyledEyebrow small color={Colors.GrayDark}>
            {eyebrow}
          </StyledEyebrow>
          <StyledTitle large color={Colors.GrayVeryDark}>
            {title}
          </StyledTitle>
          {!!link && (
            <StyledLink>
              <Icon name="copy" color={Colors.PrimaryBlue} tiny />
              <Link medium onClick={onClick} variant="text">{children}</Link>
            </StyledLink>
          )}
          {subtitle && (
            <StyledSubtitle small isMobile={isMobile} color={Colors.GrayVeryDark}>
              {subtitle}
            </StyledSubtitle>
          )}
          {!!tag && (
            <StyledTag>
              <Tag {...tag} />
            </StyledTag>
          )}
          {/** WHEN IN COLUMN VIEW */}
          {
            !!buttonGroup
            && isMobile
            && (
              <StyledButtonGroupBottom stacked={isMobile} {...buttonGroup} />
            )
          }
          {!!listItems && renderListItems(listItems)}
        </TextSection>
      </ContentWrapper>
    </StyledCard>
  );
};

export default (withAppManager(CardProfile): React$ComponentType<ManagedProps<Props>>);
